// src/services/teamLocationApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL,
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const teamLocationApi = createApi({
  reducerPath: 'teamLocationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/team-locations`,
    prepareHeaders: (headers) => {
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['TeamLocations', 'LocationStats', 'TeamAvailability', 'Teams', 'SingleTeam', 'LocationHistory'],
  endpoints: (builder) => ({
    // Get team locations (legacy endpoint)
    getTeamLocations: builder.query({
      query: ({ businessId, status, project }) => ({
        url: '/',
        params: { businessId, status, project }
      }),
      providesTags: ['TeamLocations']
    }),

     // Update team location
     updateTeamLocation: builder.mutation({
        query: ({ teamId, businessId, ...locationData }) => ({
          url: `/${teamId}/location`,
          method: 'POST',
          params: { businessId },
          body: locationData
        }),
        invalidatesTags: ['TeamLocations', 'LocationStats', 'SingleTeam']
      }),

      
    // Get team location history
    getTeamLocationHistory: builder.query({
      query: ({ businessId, teamId, limit, startDate, endDate }) => ({
        url: `/${teamId}/location-history`,
        params: { 
          businessId, 
          limit: limit || 100,
          startDate,
          endDate
        }
      }),
      // @ts-ignore
      providesTags: (result, error, { teamId }) => [
        { type: 'LocationHistory', id: teamId },
        'LocationHistory'
      ]
    }),

    // Get location statistics
    getLocationStats: builder.query({
      query: ({ businessId }) => ({
        url: '/stats',
        params: { businessId }
      }),
      providesTags: ['LocationStats']
    }),

    // Track route progress
    trackRouteProgress: builder.mutation({
      query: ({ teamId, businessId, ...progressData }) => ({
        url: `/teams/${teamId}/route-progress`,
        method: 'POST',
        params: { businessId },
        body: progressData
      }),
      invalidatesTags: ['TeamLocations', 'SingleTeam']
    }),

    // Export location data
    exportLocationData: builder.mutation({
      query: ({ businessId }) => ({
        url: '/export',
        params: { businessId },
        responseHandler: (response) => response.blob()
      })
    }),

    // Get team availability
    getTeamAvailability: builder.query({
      query: ({ businessId, teamId }) => ({
        url: '/availability',
        params: { businessId, teamId }
      }),
      providesTags: ['TeamAvailability']
    }),

    // ============================================================================
    // NEW ENHANCED TEAM ENDPOINTS
    // ============================================================================

    // Get all teams (enhanced)
    getTeams: builder.query({
      query: ({ businessId }) => ({
        url: '/teams',
        params: { businessId }
      }),
      providesTags: ['Teams']
    }),

    // Create team
    createTeam: builder.mutation({
      query: ({ businessId, ...teamData }) => ({
        url: '/teams',
        method: 'POST',
        params: { businessId },
        body: teamData
      }),
      invalidatesTags: ['Teams']
    }),
  })
});

export const {
  // Legacy endpoints,
  useGetTeamLocationsQuery,
  useGetLocationStatsQuery,
  useTrackRouteProgressMutation,
  useExportLocationDataMutation,
  useGetTeamAvailabilityQuery,
  useUpdateTeamLocationMutation,
  
  // Location history
  useGetTeamLocationHistoryQuery,
  
  // New enhanced team endpoints
  useGetTeamsQuery,
  useCreateTeamMutation,
} = teamLocationApi;